import React, { useState } from 'react'; 
import { Box, Button, Typography, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, FormControl, FormControlLabel, Switch, Checkbox, Pagination, Autocomplete, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Tabs, Tab } from '@mui/material';
import { LicenseInfo } from '@mui/x-license-pro';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TransformIcon from '@mui/icons-material/Transform';
import * as XLSX from 'xlsx';
import verificareToken from '../VerificareToken';
import FileUpload from '../FileUpload'; 

LicenseInfo.setLicenseKey(
  '6d6829e50ec611ef52701c7161ea0bd1Tz0xMTQ0NzAsRT0xNzgxMzA4Nzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
);

const ImportCustom = () => {
  const { token } = verificareToken();
  const [excelData, setExcelData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [selectedColumns, setSelectedColumns] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [step, setStep] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 100;

  // Multi-sheet support
  const [sheetsData, setSheetsData] = useState({}); // { sheetName: { columns, rows } }
  const [sheetNames, setSheetNames] = useState([]);
  const [activeSheet, setActiveSheet] = useState('');
  const [selectedSheets, setSelectedSheets] = useState({}); // { sheetName: true/false }
  const [sheetMappings, setSheetMappings] = useState({}); // { sheetName: { columnMapping, selectedColumns, selectedRows } }
  
  // Find & Replace
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [findResults, setFindResults] = useState(null);
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  
  // Filtru global
  const [filterText, setFilterText] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  
  // Sortare
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' sau 'desc'
  
  // Transformări
  const [selectedTransformColumn, setSelectedTransformColumn] = useState('');
  const [selectedTransformation, setSelectedTransformation] = useState('');
  
  // Users pentru maparea Jurist
  const [users, setUsers] = useState([]);
  const [rowUserAssignments, setRowUserAssignments] = useState({}); // { rowIndex: userId }
  const [globalUserAssignment, setGlobalUserAssignment] = useState(null);
  const [showUserAssignment, setShowUserAssignment] = useState(false); // Toggle pentru a arăta assignment-ul

  // Generare automată număr intern
  const [autoGenerateNumarIntern, setAutoGenerateNumarIntern] = useState(false);

  // Monitorizat / Sincronizat per rând (per sheet)
  const [rowMonitorizat, setRowMonitorizat] = useState({}); // { rowIndex: 0/1 } - sheet-ul activ
  const [rowSincronizat, setRowSincronizat] = useState({}); // { rowIndex: 0/1 } - sheet-ul activ
  const [globalMonitorizat, setGlobalMonitorizat] = useState(false);
  const [globalSincronizat, setGlobalSincronizat] = useState(false);
  const [sheetMonitorizat, setSheetMonitorizat] = useState({}); // { sheetName: { rowIndex: 0/1 } }
  const [sheetSincronizat, setSheetSincronizat] = useState({}); // { sheetName: { rowIndex: 0/1 } }

  const handleGlobalMonitorizatChange = (checked) => {
    setGlobalMonitorizat(checked);
    const newValues = {};
    rows.forEach((_, index) => { newValues[index] = checked ? 1 : 0; });
    setRowMonitorizat(newValues);
  };

  const handleGlobalSincronizatChange = (checked) => {
    setGlobalSincronizat(checked);
    const newValues = {};
    rows.forEach((_, index) => { newValues[index] = checked ? 1 : 0; });
    setRowSincronizat(newValues);
  };

  // Câmpuri custom din administrare
  const [customFields, setCustomFields] = useState([]);

  // Fetch câmpuri custom
  React.useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await fetch(`${window.API_URL}customFields`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data?.data?.customFields) {
          setCustomFields(data.data.customFields.filter(f => f.active === 1 || f.active === "1"));
        }
      } catch (error) {
        console.error("Eroare la preluarea câmpurilor custom", error);
      }
    };

    if (token) {
      fetchCustomFields();
    }
  }, [token]);

  // Lista coloanelor interne disponibile (cu câmpuri custom dinamice)
  const baseColumns = [
    { value: '', label: '-- Neselectat --', group: 'Sistem' },
    { value: 'numarDosar', label: 'Număr dosar', group: 'Dosar' },
    { value: 'numarIntern', label: 'Număr intern', group: 'Dosar' },
    { value: 'tipDosar', label: 'Tip dosar', group: 'Dosar' },
    { value: 'dataDosar', label: 'Dată dosar', group: 'Dosar' },
    { value: 'valoareDosar', label: 'Valoare dosar', group: 'Dosar' },
    { value: 'valoareRecuperata', label: 'Valoare recuperată', group: 'Dosar' },
    { value: 'materieDosar', label: 'Materie dosar', group: 'Dosar' },
    { value: 'obiectDosar', label: 'Obiect dosar', group: 'Dosar' },
    { value: 'stareDosar', label: 'Stare dosar', group: 'Dosar' },
    { value: 'solutieDosar', label: 'Soluție dosar', group: 'Dosar' },
    { value: 'detaliiSolutie', label: 'Detalii soluție', group: 'Dosar' },
    { value: 'dataSolutionare', label: 'Dată soluționare', group: 'Dosar' },
    { value: 'jurist', label: 'Jurist', group: 'Dosar' },
    { value: 'calitateClientDosar', label: 'Calitate client dosar', group: 'Client' },
    { value: 'adresaClientDosar', label: 'Adresă client dosar', group: 'Client' },
    { value: 'cnpClientDosar', label: 'CNP Client dosar', group: 'Client' },
    { value: 'cuiClientDosar', label: 'CUI client dosar', group: 'Client' },
    { value: 'recomClientDosar', label: 'RECOM client dosar', group: 'Client' },
    { value: 'ibanClientDosar', label: 'IBAN client dosar', group: 'Client' },
    { value: 'utilizatorDosar', label: 'Utilizator dosar', group: 'Dosar' },
    { value: 'instantaDosar', label: 'Instanță dosar', group: 'Dosar' },
    { value: 'dataDosar2', label: 'Dată dosar', group: 'Dosar' },
    { value: 'dosareConexe', label: 'Dosare conexe', group: 'Dosar' },
    { value: 'observatii', label: 'Observații', group: 'Dosar' },
    { value: 'departament', label: 'Departament', group: 'Dosar' },
    { value: 'rol', label: 'Rol', group: 'Dosar' },
    { value: 'parteDosar', label: 'Parte dosar', group: 'Părți' },
    { value: 'parteAdversa', label: 'Parte adversă', group: 'Părți' },
    { value: 'calitateParteAdversa', label: 'Calitate parte adversă', group: 'Părți' },
    { value: 'clientDosar', label: 'Client dosar', group: 'Client' },
  ];

  // Construim lista completă cu câmpurile custom din API
  const availableColumns = React.useMemo(() => {
    const customFieldColumns = customFields.map(field => ({
      value: `customField_${field.name}`,
      label: `${field.label} (${field.type})`,
      group: '📋 Câmpuri personalizate',
      isCustomField: true,
      customFieldName: field.name,
      customFieldType: field.type,
      customFieldId: field.id
    }));
    // Sortăm după grup ca MUI Autocomplete să grupeze corect (elementele din același grup trebuie să fie consecutive)
    const all = [...baseColumns, ...customFieldColumns];
    const groupOrder = ['Sistem', 'Dosar', 'Client', 'Părți', '📋 Câmpuri personalizate'];
    return all.sort((a, b) => {
      const idxA = groupOrder.indexOf(a.group ?? '');
      const idxB = groupOrder.indexOf(b.group ?? '');
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });
  }, [customFields]);
 
  const handleReset = () => {
    setExcelData(null);
    setColumns([]);
    setRows([]);
    setColumnMapping({});
    setSelectedColumns({});
    setSelectedRows({});
    setStep(1);
    setCurrentPage(1);
    setFindText('');
    setReplaceText('');
    setFindResults(null);
    setFilterText('');
    setFilteredRows([]);
    setRowUserAssignments({});
    setGlobalUserAssignment(null);
    setShowUserAssignment(false);
    setSheetsData({});
    setSheetNames([]);
    setActiveSheet('');
    setSelectedSheets({});
    setSheetMappings({});
    setAutoGenerateNumarIntern(false);
    setRowMonitorizat({});
    setRowSincronizat({});
    setGlobalMonitorizat(false);
    setGlobalSincronizat(false);
    setSheetMonitorizat({});
    setSheetSincronizat({});
  };

  // Fetch users
  React.useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(`${window.API_URL}users?users_from_all_common_groups=1`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const { data } = await response.json();
        setUsers(data.users);
      } catch (error) {
        console.error("Eroare la preluarea utilizatorilor", error);
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  const handleColumnMappingChange = (excelColumn, internalColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [excelColumn]: internalColumn
    }));
  };

  const handleColumnSelectionChange = (excelColumn, isSelected) => {
    setSelectedColumns(prev => ({
      ...prev,
      [excelColumn]: isSelected
    }));
  };

  const handleColumnHeaderClick = (excelColumn) => {
    setSelectedColumns(prev => ({
      ...prev,
      [excelColumn]: !prev[excelColumn]
    }));
  };

  const handleRowSelectionChange = (rowIndex, isSelected) => {
    setSelectedRows(prev => ({
      ...prev,
      [rowIndex]: isSelected
    }));
  };

  const handleSelectAllRows = () => {
    const allSelected = Object.keys(selectedRows).length === rows.length && 
                        Object.values(selectedRows).every(Boolean);
    const newSelectedRows = {};
    rows.forEach((_, index) => {
      newSelectedRows[index] = !allSelected;
    });
    setSelectedRows(newSelectedRows);
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Determină câte rânduri să afișeze cu paginare
  const getDisplayRows = () => {
    // Aplică sortarea mai întâi
    const sortedData = getSortedRows();
    // Apoi filtrarea
    const dataToDisplay = filterText ? sortedData.filter(row => {
      return columns.some(col => {
        const cellValue = String(row[col] || '');
        return cellValue.toLowerCase().includes(filterText.toLowerCase());
      });
    }) : sortedData;
    
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return dataToDisplay.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const sortedData = getSortedRows();
    const dataToDisplay = filterText ? sortedData.filter(row => {
      return columns.some(col => {
        const cellValue = String(row[col] || '');
        return cellValue.toLowerCase().includes(filterText.toLowerCase());
      });
    }) : sortedData;
    return Math.ceil(dataToDisplay.length / rowsPerPage);
  };

  const getActualRowIndex = (displayIndex) => {
    const displayRow = getDisplayRows()[displayIndex];
    return rows.findIndex(row => row === displayRow);
  };

  // Verifică dacă poate merge la pasul următor
  const canProceedToNextStep = () => {
    // Verifică dacă există cel puțin o coloană selectată
    const hasSelectedColumn = Object.values(selectedColumns).some(Boolean);
    if (!hasSelectedColumn) return false;

    // Verifică dacă cel puțin o coloană selectată are mapare setată (nu '')
    const hasValidMapping = columns.some(col => 
      selectedColumns[col] && columnMapping[col] && columnMapping[col] !== ''
    );
    if (!hasValidMapping) return false;

    // Verifică dacă există cel puțin un rând selectat
    const hasSelectedRow = Object.values(selectedRows).some(Boolean);
    if (!hasSelectedRow) return false;

    return true;
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const names = workbook.SheetNames;
      
      // Parsăm toate sheet-urile
      const allSheetsData = {};
      const allSheetMappings = {};
      const allSelectedSheets = {};

      names.forEach(name => {
        const sheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        const headerRow = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0] || [];
        const columnNames = headerRow.filter(col => col !== undefined && col !== null && String(col).trim() !== '').map(col => String(col));

        allSheetsData[name] = { columns: columnNames, rows: jsonData };
        allSelectedSheets[name] = true; // implicit toate bifate

        // Inițializăm mapările per sheet
        const initialColSelection = {};
        columnNames.forEach(col => { initialColSelection[col] = false; });
        const initialRowSelection = {};
        jsonData.forEach((_, index) => { initialRowSelection[index] = true; });

        allSheetMappings[name] = {
          columnMapping: {},
          selectedColumns: initialColSelection,
          selectedRows: initialRowSelection
        };
      });

      setSheetsData(allSheetsData);
      setSheetNames(names);
      setSelectedSheets(allSelectedSheets);
      setSheetMappings(allSheetMappings);

      // Setăm primul sheet ca activ
      const firstName = names[0];
      setActiveSheet(firstName);
      setColumns(allSheetsData[firstName].columns);
      setRows(allSheetsData[firstName].rows);
      setColumnMapping({});
      setSelectedColumns(allSheetMappings[firstName].selectedColumns);
      setSelectedRows(allSheetMappings[firstName].selectedRows);
      setExcelData(true);
    };
    reader.readAsArrayBuffer(file);
  };

  // Salvează starea curentă a sheet-ului activ și schimbă tab-ul
  const handleSheetChange = (newSheetName) => {
    if (newSheetName === activeSheet) return;

    // Salvăm starea sheet-ului curent (inclusiv rândurile modificate de transformări)
    setSheetMappings(prev => ({
      ...prev,
      [activeSheet]: {
        columnMapping: { ...columnMapping },
        selectedColumns: { ...selectedColumns },
        selectedRows: { ...selectedRows }
      }
    }));
    setSheetsData(prev => ({
      ...prev,
      [activeSheet]: { ...prev[activeSheet], rows: rows }
    }));
    // Salvăm monitorizat/sincronizat al sheet-ului curent
    setSheetMonitorizat(prev => ({ ...prev, [activeSheet]: { ...rowMonitorizat } }));
    setSheetSincronizat(prev => ({ ...prev, [activeSheet]: { ...rowSincronizat } }));

    // Încărcăm starea noului sheet
    const sheetData = sheetsData[newSheetName];
    const sheetMapping = sheetMappings[newSheetName] || {};

    setActiveSheet(newSheetName);
    setColumns(sheetData.columns);
    setRows(sheetData.rows);
    setColumnMapping(sheetMapping.columnMapping || {});
    setSelectedColumns(sheetMapping.selectedColumns || {});
    setSelectedRows(sheetMapping.selectedRows || {});
    setRowMonitorizat(sheetMonitorizat[newSheetName] || {});
    setRowSincronizat(sheetSincronizat[newSheetName] || {});
    setGlobalMonitorizat(false);
    setGlobalSincronizat(false);
    setCurrentPage(1);
    setFilterText('');
    setSortColumn(null);
  };

  const handleSheetSelectionToggle = (sheetName) => {
    const newValue = !selectedSheets[sheetName];
    setSelectedSheets(prev => ({ ...prev, [sheetName]: newValue }));

    const sheetRowCount = sheetsData[sheetName]?.rows?.length || 0;
    const newRowSelections = {};
    for (let i = 0; i < sheetRowCount; i++) {
      newRowSelections[i] = newValue; // true = selectat, false = deselectat
    }

    if (sheetName === activeSheet) {
      setSelectedRows(prev => ({ ...prev, ...newRowSelections }));
    } else {
      setSheetMappings(prev => ({
        ...prev,
        [sheetName]: {
          ...(prev[sheetName] || {}),
          selectedRows: newRowSelections
        }
      }));
    }
  };

  // Traduce numele generic de sheet (Sheet1 → Foaie 1, Foaie1 → Foaie 1)
  const translateSheetName = (name) => {
    const matchSheet = name.match(/^Sheet(\d+)$/i);
    if (matchSheet) return `Foaie ${matchSheet[1]}`;
    const matchFoaie = name.match(/^Foaie(\d+)$/i);
    if (matchFoaie) return `Foaie ${matchFoaie[1]}`;
    return name;
  };

  // Filtrează coloanele selectate pentru pasul 2
  const getSelectedColumnsData = () => {
    return columns.filter(col => selectedColumns[col]);
  };

  const getMappedColumnLabel = (excelColumn) => {
    const mappedValue = columnMapping[excelColumn];
    if (!mappedValue) return '-- Neselectat --';
    const found = availableColumns.find(col => col.value === mappedValue);
    return found ? found.label : '-- Neselectat --';
  };

  // Generează JSON-ul pentru import (per sheet sau sheet-ul curent)
  const generateImportJsonForSheet = (sheetName) => {
    const sheetData = sheetsData[sheetName];
    const mapping = sheetName === activeSheet 
      ? { columnMapping, selectedColumns, selectedRows }
      : (sheetMappings[sheetName] || { columnMapping: {}, selectedColumns: {}, selectedRows: {} });

    const sheetColumns = sheetData.columns;
    const sheetRows = sheetData.rows;
    const sheetColumnMapping = mapping.columnMapping;
    const sheetSelectedColumns = mapping.selectedColumns;
    const sheetSelectedRows = mapping.selectedRows;

    // Filtrează doar coloanele selectate și mapate
    const selectedMappedColumns = sheetColumns.filter(col => 
      sheetSelectedColumns[col] && sheetColumnMapping[col] && sheetColumnMapping[col] !== ''
    );

    // Filtrează doar rândurile selectate
    const selectedRowsData = sheetRows.filter((_, index) => sheetSelectedRows[index]);

    // Identifică câmpurile custom mapate
    const customFieldMappingsLocal = {};
    selectedMappedColumns.forEach(excelCol => {
      const internalField = sheetColumnMapping[excelCol];
      if (internalField && internalField.startsWith('customField_')) {
        const colDef = availableColumns.find(c => c.value === internalField);
        if (colDef && colDef.isCustomField) {
          customFieldMappingsLocal[internalField] = {
            fieldName: colDef.customFieldName,
            fieldType: colDef.customFieldType,
            fieldId: colDef.customFieldId,
            excelColumn: excelCol
          };
        }
      }
    });

    // Construiește datele mapate
    const mappedData = selectedRowsData.map((row) => {
      const mappedRow = {};
      const customFieldsData = [];

      selectedMappedColumns.forEach(excelCol => {
        const internalField = sheetColumnMapping[excelCol];
        if (internalField && internalField.startsWith('customField_') && customFieldMappingsLocal[internalField]) {
          const cfInfo = customFieldMappingsLocal[internalField];
          customFieldsData.push({
            field_name: cfInfo.fieldName,
            field_type: cfInfo.fieldType,
            field_id: cfInfo.fieldId,
            value: row[excelCol]
          });
        } else {
          mappedRow[internalField] = row[excelCol];
        }
      });

      if (customFieldsData.length > 0) {
        mappedRow.customFields = customFieldsData;
      }

      const actualRowIndex = sheetRows.indexOf(row);
      if (shouldShowUserAssignment() && rowUserAssignments[actualRowIndex]) {
        mappedRow.assignedUserId = rowUserAssignments[actualRowIndex];
      }

      // Monitorizat și Sincronizat (per sheet)
      const monit = sheetName === activeSheet ? rowMonitorizat : (sheetMonitorizat[sheetName] || {});
      const sincr = sheetName === activeSheet ? rowSincronizat : (sheetSincronizat[sheetName] || {});
      mappedRow.monitorizat = monit[actualRowIndex] || 0;
      mappedRow.sincronizat = sincr[actualRowIndex] || 0;

      return mappedRow;
    });

    return {
      totalRows: selectedRowsData.length,
      totalColumns: selectedMappedColumns.length,
      sheetName: sheetName,
      columnMapping: selectedMappedColumns.reduce((acc, col) => {
        acc[col] = sheetColumnMapping[col];
        return acc;
      }, {}),
      customFieldMappings: Object.keys(customFieldMappingsLocal).length > 0 ? customFieldMappingsLocal : undefined,
      hasJuristMapping: Object.values(sheetColumnMapping).includes('jurist'),
      hasManualUserAssignment: showUserAssignment,
      autoGenerateNumarIntern: autoGenerateNumarIntern ? 1 : 0,
      data: mappedData
    };
  };

  const generateImportJson = () => {
    // Salvăm starea sheet-ului activ
    const currentMappings = {
      ...sheetMappings,
      [activeSheet]: { columnMapping, selectedColumns, selectedRows }
    };

    // Dacă e un singur sheet sau doar unul bifat, generăm ca înainte
    const sheetsToImport = sheetNames.filter(name => selectedSheets[name]);
    
    if (sheetsToImport.length === 1) {
      const payload = generateImportJsonForSheet(sheetsToImport[0]);
      return JSON.stringify(payload, null, 2);
    }

    // Multi-sheet: combinăm datele din toate sheet-urile bifate
    let allData = [];
    const allColumnMappings = {};

    sheetsToImport.forEach(name => {
      const sheetPayload = generateImportJsonForSheet(name);
      allData = [...allData, ...sheetPayload.data];
      allColumnMappings[name] = sheetPayload.columnMapping;
    });

    const jsonPayload = {
      totalRows: allData.length,
      sheetsProcessed: sheetsToImport.length,
      sheetNames: sheetsToImport,
      columnMappings: allColumnMappings,
      hasJuristMapping: isJuristColumnMapped(),
      hasManualUserAssignment: showUserAssignment,
      autoGenerateNumarIntern: autoGenerateNumarIntern ? 1 : 0,
      data: allData
    };

    return JSON.stringify(jsonPayload, null, 2);
  };

  const [importProgress, setImportProgress] = useState(null); // null | 'loading' | 'success' | 'error' | 'cancelled'
  const importCancelledRef = React.useRef(false);
  const [importMessage, setImportMessage] = useState('');
  const [importPercent, setImportPercent] = useState(0);
  const [importCompleted, setImportCompleted] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importErrors, setImportErrors] = useState([]);
  const [retryingRow, setRetryingRow] = useState(null);

  const handleStartImport = async () => {
    const json = generateImportJson();
    const payload = JSON.parse(json);
    const allData = payload.data;
    const totalRows = allData.length;
    const chunkSize = 10;

    importCancelledRef.current = false;
    setImportProgress('loading');
    setImportMessage('');
    setImportPercent(0);
    setImportCompleted(0);
    setImportTotal(totalRows);
    setImportErrors([]);

    let processedCount = 0;
    const errorList = [];

    // Trimitem câte 1 request per dosar, dar maxim 10 simultan (în paralel)
    for (let i = 0; i < totalRows; i += chunkSize) {
      // Verificăm dacă s-a cerut anularea
      if (importCancelledRef.current) {
        setImportProgress('cancelled');
        setImportMessage(`Import oprit manual. ${processedCount} dosare procesate din ${totalRows}.`);
        return;
      }

      const chunk = allData.slice(i, i + chunkSize);

      const promises = chunk.map(async (dosarData, idx) => {
        const rowIndex = i + idx;
        try {
          const response = await fetch(`${window.API_URL}importCustom`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              ...payload,
              totalRows: 1,
              data: [dosarData]
            })
          });

          const responseData = await response.json();

          if (response.ok) {
            return { success: true, rowIndex };
          } else {
            return { success: false, rowIndex, message: responseData.message || 'Nu a putut fi importat', dosarData };
          }
        } catch (error) {
          return { success: false, rowIndex, message: 'Problemă de conexiune', dosarData };
        }
      });

      const results = await Promise.all(promises);

      results.forEach(result => {
        processedCount++;
        if (!result.success) {
          errorList.push({
            row: result.rowIndex + 1,
            message: result.message,
            dosarData: result.dosarData
          });
        }
      });

      // Actualizăm progresul
      setImportCompleted(processedCount);
      setImportPercent(Math.round((processedCount / totalRows) * 100));
      setImportErrors([...errorList]);

      // Pauză de 500ms între calupuri (nu la ultimul)
      if (i + chunkSize < totalRows) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    // Final
    const successCount = processedCount - errorList.length;
    if (errorList.length === 0) {
      setImportProgress('success');
      setImportMessage(`Import realizat cu succes! ${totalRows} dosare procesate.`);
    } else if (successCount > 0) {
      setImportProgress('success');
      setImportMessage(`${successCount} dosare importate cu succes. ${errorList.length} dosare necesită atenție.`);
    } else {
      setImportProgress('error');
      setImportMessage('Dosarele nu au putut fi importate. Verificați conexiunea și reîncercați.');
    }
  };

  const handleCloseJsonModal = () => {
    importCancelledRef.current = false;
    setImportProgress(null);
    setImportMessage('');
    setImportPercent(0);
    setImportCompleted(0);
    setImportTotal(0);
    setImportErrors([]);
    setRetryingRow(null);
  };

  const handleRetryImport = async (errorItem) => {
    setRetryingRow(errorItem.row);
    const json = generateImportJson();
    const payload = JSON.parse(json);

    try {
      const response = await fetch(`${window.API_URL}importCustom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...payload,
          totalRows: 1,
          data: [errorItem.dosarData]
        })
      });

      const responseData = await response.json();

      if (response.ok) {
        // Eliminăm din lista de atenționări
        setImportErrors(prev => prev.filter(e => e.row !== errorItem.row));
        setImportCompleted(prev => prev); // forțăm re-render
      } else {
        // Actualizăm mesajul
        setImportErrors(prev => prev.map(e =>
          e.row === errorItem.row ? { ...e, message: responseData.message || 'Nu a putut fi importat' } : e
        ));
      }
    } catch (error) {
      setImportErrors(prev => prev.map(e =>
        e.row === errorItem.row ? { ...e, message: 'Problemă de conexiune' } : e
      ));
    } finally {
      setRetryingRow(null);
    }
  };

  // Find & Replace functions
  const handleFind = () => {
    if (!findText) return;
    
    let count = 0;
    const locations = [];
    
    rows.forEach((row, rowIndex) => {
      columns.forEach(col => {
        const cellValue = String(row[col] || '');
        if (cellValue.toLowerCase().includes(findText.toLowerCase())) {
          count++;
          locations.push({ row: rowIndex, col, value: cellValue });
        }
      });
    });
    
    setFindResults({ count, locations });
    if (count > 0) {
      setShowReplaceDialog(true);
    } else {
      alert('Nu s-au găsit rezultate.');
    }
  };

  const handleReplace = () => {
    if (!findResults || findResults.count === 0) return;
    
    const newRows = [...rows];
    findResults.locations.forEach(({ row, col }) => {
      const cellValue = String(newRows[row][col] || '');
      const regex = new RegExp(findText, 'gi');
      newRows[row][col] = cellValue.replace(regex, replaceText);
    });
    
    setRows(newRows);
    setShowReplaceDialog(false);
    setFindResults(null);
    setFindText('');
    setReplaceText('');
    alert(`${findResults.count} înlocuiri efectuate cu succes!`);
  };

  const handleCancelReplace = () => {
    setShowReplaceDialog(false);
    setFindResults(null);
  };

  // Filter functions
  const handleFilterChange = (value) => {
    setFilterText(value);
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setFilterText('');
    setFilteredRows([]);
    setCurrentPage(1);
  };

  const getTotalRowsCount = () => {
    const sortedData = getSortedRows();
    if (!filterText) return sortedData.length;
    
    return sortedData.filter(row => {
      return columns.some(col => {
        const cellValue = String(row[col] || '');
        return cellValue.toLowerCase().includes(filterText.toLowerCase());
      });
    }).length;
  };

  // Sortare
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Schimbă direcția dacă e aceeași coloană
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouă coloană, sortare ascendentă
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedRows = () => {
    if (!sortColumn) return rows;
    
    const sorted = [...rows].sort((a, b) => {
      const aVal = String(a[sortColumn] || '');
      const bVal = String(b[sortColumn] || '');
      
      if (sortDirection === 'asc') {
        return aVal.localeCompare(bVal, 'ro', { numeric: true });
      } else {
        return bVal.localeCompare(aVal, 'ro', { numeric: true });
      }
    });
    
    return sorted;
  };

  // Transformări
  const transformations = [
    { value: '', label: '-- Selectați transformare --' },
    { value: 'uppercase', label: 'MAJUSCULE' },
    { value: 'lowercase', label: 'minuscule' },
    { value: 'capitalize', label: 'Prima Literă Mare' },
    { value: 'trim', label: 'Elimină spații' },
    { value: 'removeSpaces', label: 'Elimină toate spațiile' },
  ];

  const applyTransformation = () => {
    if (!selectedTransformColumn || !selectedTransformation) return;
    
    const newRows = rows.map(row => {
      const newRow = { ...row };
      const cellValue = String(row[selectedTransformColumn] || '');
      
      switch (selectedTransformation) {
        case 'uppercase':
          newRow[selectedTransformColumn] = cellValue.toUpperCase();
          break;
        case 'lowercase':
          newRow[selectedTransformColumn] = cellValue.toLowerCase();
          break;
        case 'capitalize':
          newRow[selectedTransformColumn] = cellValue
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          break;
        case 'trim':
          newRow[selectedTransformColumn] = cellValue.trim();
          break;
        case 'removeSpaces':
          newRow[selectedTransformColumn] = cellValue.replace(/\s+/g, '');
          break;
        default:
          break;
      }
      
      return newRow;
    });
    
    setRows(newRows);
    setSelectedTransformColumn('');
    setSelectedTransformation('');
    alert('Transformare aplicată cu succes!');
  };

  // Verifică dacă coloana Jurist este mapată
  const isJuristColumnMapped = () => {
    return Object.values(columnMapping).includes('jurist');
  };

  // Verifică dacă coloana Jurist este mapată SAU dacă user-ul a activat manual assignment-ul
  const shouldShowUserAssignment = () => {
    return isJuristColumnMapped() || showUserAssignment;
  };

  // Găsește coloana Excel care e mapată la Jurist
  const getJuristExcelColumn = () => {
    return Object.keys(columnMapping).find(key => columnMapping[key] === 'jurist');
  };

  // Handler pentru assignment global de user
  const handleGlobalUserAssignment = (userId) => {
    setGlobalUserAssignment(userId);
    // Aplică la toate rândurile selectate
    const newAssignments = {};
    rows.forEach((_, index) => {
      if (selectedRows[index]) {
        newAssignments[index] = userId;
      }
    });
    setRowUserAssignments(prev => ({ ...prev, ...newAssignments }));
  };

  // Handler pentru assignment individual de user
  const handleRowUserAssignment = (rowIndex, userId) => {
    setRowUserAssignments(prev => ({
      ...prev,
      [rowIndex]: userId
    }));
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', width: '100%' }}>
      <Box sx={{ flex: 1, p: 3, maxWidth: '100%', overflow: 'hidden' }}>
        <Typography variant="h5" gutterBottom>
          Import Custom - Vizualizare Date Excel {step === 2 && '- Pasul 2: Confirmare Mapări'}{step === 3 && '- Pasul 3: Previzualizare date'}
        </Typography>
        
        {step === 1 && (
          <>
            <Alert severity="info" variant="standard" sx={{ mb: 2 }}> 
              Selectați un fișier Excel pentru a vizualiza toate coloanele și datele.
            </Alert>

            <FileUpload onFileUpload={handleFileUpload} />

            {excelData && (
              <>
                <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                  Fișier procesat cu succes: {rows.length} rânduri și {columns.length} coloane identificate.
                  {sheetNames.length > 1 && ` (${sheetNames.length} foi detectate)`}
                </Alert>

                {!canProceedToNextStep() && (
                  <Alert severity="warning" sx={{ mb: 1 }} variant="standard">
                    Pentru a continua, trebuie să:
                    {!Object.values(selectedColumns).some(Boolean) && (
                      <li>Selectați cel puțin o coloană</li>
                    )}
                    {Object.values(selectedColumns).some(Boolean) && 
                     !columns.some(col => selectedColumns[col] && columnMapping[col] && columnMapping[col] !== '') && (
                      <li>Setați maparea pentru cel puțin o coloană selectată</li>
                    )}
                    {!Object.values(selectedRows).some(Boolean) && (
                      <li>Selectați cel puțin un rând din tabel</li>
                    )}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleReset}
                    startIcon={<ReplayIcon />}
                    size="small"
                  >
                    Încarcă alt fișier
                  </Button>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0 }}>
                    <Button 
                      variant="contained" 
                      color="success" 
                      onClick={handleNextStep}
                      endIcon={<ArrowForwardIcon />}
                      disabled={!canProceedToNextStep()}
                      size="small"
                    >
                      Următorul pas
                    </Button>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoGenerateNumarIntern}
                          onChange={(e) => setAutoGenerateNumarIntern(e.target.checked)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={<Typography variant="caption" sx={{ fontSize: '11px' }}>Generează automat număr intern</Typography>}
                      sx={{ mr: 0, mt: 0.5 }}
                    />
                  </Box>
                </Box>

                {/* Taburi foi */}
                {sheetNames.length > 1 && (
                  <Box sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                        Foi ({Object.values(selectedSheets).filter(Boolean).length}/{sheetNames.length} selectate pentru import):
                      </Typography>
                    </Box>
                    <Tabs
                      value={activeSheet}
                      onChange={(e, newValue) => handleSheetChange(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ minHeight: 36 }}
                    >
                      {sheetNames.map(name => (
                        <Tab
                          key={name}
                          value={name}
                          sx={{ minHeight: 36, py: 0.5, textTransform: 'none', fontSize: '12px' }}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Checkbox
                                checked={selectedSheets[name] || false}
                                onChange={(e) => { e.stopPropagation(); handleSheetSelectionToggle(name); }}
                                size="small"
                                sx={{ p: 0 }}
                              />
                              <span style={{ fontWeight: activeSheet === name ? 'bold' : 'normal' }}>
                                {translateSheetName(name)} ({sheetsData[name]?.rows?.length || 0} rânduri)
                              </span>
                            </Box>
                          }
                        />
                      ))}
                    </Tabs>
                  </Box>
                )}

                {/* Find & Replace și Filtru */}
                <Box sx={{ mb: 1, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '11px', mb: 0.5, display: 'block' }}>
                    Instrumente de procesare date
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {/* Find & Replace */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 300 }}>
                      <TextField
                        size="small"
                        placeholder="Caută text..."
                        value={findText}
                        onChange={(e) => setFindText(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleFind()}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        placeholder="Înlocuiește cu..."
                        value={replaceText}
                        onChange={(e) => setReplaceText(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<FindReplaceIcon />}
                        onClick={handleFind}
                        disabled={!findText}
                      >
                        Caută
                      </Button>
                    </Box>

                    {/* Filtru Global */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 300 }}>
                      <TextField
                        size="small"
                        placeholder="Filtrează rânduri..."
                        value={filterText}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        sx={{ flex: 1 }}
                        InputProps={{
                          startAdornment: <FilterAltIcon sx={{ mr: 1, color: 'action.active' }} />
                        }}
                      />
                      {filterText && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ClearIcon />}
                          onClick={handleClearFilter}
                          color="error"
                        >
                          Șterge
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {/* Transformări */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TransformIcon sx={{ color: 'action.active' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                      Transformări:
                    </Typography>
                    <Autocomplete
                      options={columns}
                      value={selectedTransformColumn}
                      onChange={(event, newValue) => setSelectedTransformColumn(newValue || '')}
                      size="small"
                      sx={{ minWidth: 200 }}
                      renderInput={(params) => <TextField {...params} placeholder="Selectați coloana" />}
                    />
                    <Select
                      size="small"
                      value={selectedTransformation}
                      onChange={(e) => setSelectedTransformation(e.target.value)}
                      disabled={!selectedTransformColumn}
                      sx={{ minWidth: 200 }}
                    >
                      {transformations.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      onClick={applyTransformation}
                      disabled={!selectedTransformColumn || !selectedTransformation}
                    >
                      Aplică
                    </Button>
                  </Box>

                  {/* Dropdown global pentru Jurist */}
                  {shouldShowUserAssignment() && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                        Atribuire Jurist pentru toate rândurile selectate:
                      </Typography>
                      <Autocomplete
                        options={users}
                        getOptionLabel={(option) => option.name}
                        value={users.find(u => u.id === globalUserAssignment) || null}
                        onChange={(event, newValue) => handleGlobalUserAssignment(newValue?.id || null)}
                        size="small"
                        sx={{ minWidth: 250 }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            placeholder="Selectați utilizatorul"
                            sx={{
                              '& .MuiInputBase-root': {
                                fontSize: '0.75rem',
                                padding: '2px 4px !important'
                              },
                              '& .MuiInputBase-input': {
                                padding: '4px 8px !important',
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        )}
                        ListboxProps={{
                          style: { fontSize: '0.75rem' }
                        }}
                      />
                      {!isJuristColumnMapped() && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={() => {
                            setShowUserAssignment(false);
                            setRowUserAssignments({});
                            setGlobalUserAssignment(null);
                          }}
                        >
                          Ascunde
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Buton pentru a activa assignment manual când Jurist nu e mapat */}
                  {!isJuristColumnMapped() && !showUserAssignment && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        variant="contained"
                        color="info"
                        size="small"
                        onClick={() => setShowUserAssignment(true)}
                        startIcon={<PlayArrowIcon />}
                      >
                        Asociază dosarele la utilizatori
                      </Button>
                    </Box>
                  )}

                  {filterText && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Filtru activ: {getTotalRowsCount()} rânduri găsite din {rows.length} total
                    </Alert>
                  )}
                </Box>

                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    maxHeight: 'calc(100vh - 400px)',
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'auto',
                    opacity: selectedSheets[activeSheet] === false ? 0.4 : 1,
                    pointerEvents: selectedSheets[activeSheet] === false ? 'none' : 'auto',
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  <Table stickyHeader sx={{ width: '100%', tableLayout: 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold', 
                            backgroundColor: '#2c3e50 !important', 
                            color: 'white',
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            borderRight: '1px solid #34495e',
                            textAlign: 'center'
                          }}
                        >
                          <Checkbox
                            checked={Object.keys(selectedRows).length === rows.length && Object.values(selectedRows).every(Boolean)}
                            indeterminate={Object.values(selectedRows).some(Boolean) && !Object.values(selectedRows).every(Boolean)}
                            onChange={handleSelectAllRows}
                            sx={{
                              color: 'white',
                              padding: 0,
                              '&.Mui-checked': {
                                color: '#4caf50',
                              },
                              '&.MuiCheckbox-indeterminate': {
                                color: '#ff9800',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#2c3e50 !important',
                            color: 'white',
                            position: 'sticky',
                            left: 42,
                            zIndex: 3,
                            borderRight: '1px solid #34495e',
                            textAlign: 'center',
                            minWidth: 70,
                            padding: '4px'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '10px', color: 'white', display: 'block' }}>Monit.</Typography>
                          <Switch
                            checked={globalMonitorizat}
                            onChange={(e) => handleGlobalMonitorizatChange(e.target.checked)}
                            size="small"
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: '#2c3e50 !important',
                            color: 'white',
                            position: 'sticky',
                            left: 112,
                            zIndex: 3,
                            borderRight: '1px solid #34495e',
                            textAlign: 'center',
                            minWidth: 70,
                            padding: '4px'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '10px', color: 'white', display: 'block' }}>Sincr.</Typography>
                          <Switch
                            checked={globalSincronizat}
                            onChange={(e) => handleGlobalSincronizatChange(e.target.checked)}
                            size="small"
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' } }}
                          />
                        </TableCell>
                        {columns.map((column, index) => (
                          <TableCell 
                            key={index}
                            sx={{ 
                              backgroundColor: selectedColumns[column] ? '#c8e6c9 !important' : '#2c3e50 !important', 
                              color: selectedColumns[column] ? '#1b5e20 !important' : 'white !important',
                              minWidth: 200,
                              maxWidth: 350,
                              whiteSpace: 'nowrap',
                              borderRight: index < columns.length - 1 ? '1px solid #34495e' : 'none',
                              padding: '8px',
                              transition: 'background-color 0.3s ease'
                            }}
                          >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  checked={selectedColumns[column] || false}
                                  onChange={(e) => handleColumnSelectionChange(column, e.target.checked)}
                                  sx={{
                                    color: selectedColumns[column] ? '#1b5e20' : 'white',
                                    padding: 0,
                                    '&.Mui-checked': {
                                      color: '#2e7d32',
                                    },
                                  }}
                                />
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 'bold', 
                                    fontSize: '0.875rem', 
                                    color: selectedColumns[column] ? '#1b5e20 !important' : 'white !important',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    '&:hover': {
                                      textDecoration: 'underline'
                                    }
                                  }}
                                  onClick={() => handleColumnHeaderClick(column)}
                                >
                                  {column}
                                </Typography>
                              </Box>
                              <FormControl size="small" fullWidth>
                                <Autocomplete
                                  options={availableColumns}
                                  getOptionLabel={(option) => option.label}
                                  groupBy={(option) => option.group || ''}
                                  value={availableColumns.find(opt => opt.value === columnMapping[column]) || availableColumns[0]}
                                  onChange={(event, newValue) => handleColumnMappingChange(column, newValue?.value || '')}
                                  disabled={!selectedColumns[column]}
                                  disableClearable
                                  size="small"
                                  renderInput={(params) => (
                                    <TextField 
                                      {...params} 
                                      sx={{
                                        backgroundColor: 'white',
                                        '& .MuiInputBase-root': {
                                          fontSize: '0.75rem',
                                          padding: '0 !important'
                                        },
                                        '& .MuiInputBase-input': {
                                          padding: '4px 8px !important'
                                        }
                                      }}
                                    />
                                  )}
                                  sx={{
                                    '& .MuiAutocomplete-input': {
                                      fontSize: '0.75rem'
                                    }
                                  }}
                                  ListboxProps={{
                                    style: { fontSize: '0.75rem' }
                                  }}
                                />
                              </FormControl>
                            </Box>
                          </TableCell>
                        ))}
                        {shouldShowUserAssignment() && (
                          <TableCell 
                            sx={{ 
                              fontWeight: 'bold', 
                              backgroundColor: '#2c3e50 !important', 
                              color: 'white',
                              minWidth: 200,
                              position: 'sticky',
                              right: 0,
                              zIndex: 3,
                              borderLeft: '2px solid #34495e'
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                              Atribuire Jurist
                            </Typography>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDisplayRows().map((row, rowIndex) => {
                        const actualRowIndex = getActualRowIndex(rowIndex);
                        return (
                          <TableRow 
                            key={actualRowIndex}
                            sx={{ 
                              '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                              opacity: selectedRows[actualRowIndex] ? 1 : 0.5
                            }}
                          >
                            <TableCell 
                              sx={{ 
                                fontWeight: 'bold',
                                position: 'sticky',
                                left: 0,
                                backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f5f5f5',
                                zIndex: 1,
                                textAlign: 'center'
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                <Checkbox
                                  checked={selectedRows[actualRowIndex] || false}
                                  onChange={(e) => handleRowSelectionChange(actualRowIndex, e.target.checked)}
                                  size="small"
                                  sx={{
                                    padding: 0,
                                    '&.Mui-checked': {
                                      color: '#4caf50',
                                    },
                                  }}
                                />
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {actualRowIndex + 1}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 42,
                                backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f5f5f5',
                                zIndex: 1,
                                textAlign: 'center',
                                padding: '2px'
                              }}
                            >
                              <Switch
                                checked={rowMonitorizat[actualRowIndex] === 1}
                                onChange={(e) => setRowMonitorizat(prev => ({ ...prev, [actualRowIndex]: e.target.checked ? 1 : 0 }))}
                                size="small"
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                position: 'sticky',
                                left: 112,
                                backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f5f5f5',
                                zIndex: 1,
                                textAlign: 'center',
                                padding: '2px'
                              }}
                            >
                              <Switch
                                checked={rowSincronizat[actualRowIndex] === 1}
                                onChange={(e) => setRowSincronizat(prev => ({ ...prev, [actualRowIndex]: e.target.checked ? 1 : 0 }))}
                                size="small"
                              />
                            </TableCell>
                            {columns.map((column, colIndex) => (
                              <TableCell 
                                key={colIndex}
                                sx={{ 
                                  minWidth: 200,
                                  maxWidth: 350,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  backgroundColor: selectedColumns[column] ? '#e8f5e9' : 'inherit'
                                }}
                              >
                                {row[column] !== undefined && row[column] !== null ? String(row[column]) : '-'}
                              </TableCell>
                            ))}
                            {shouldShowUserAssignment() && (
                              <TableCell 
                                sx={{ 
                                  minWidth: 200,
                                  position: 'sticky',
                                  right: 0,
                                  backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f5f5f5',
                                  zIndex: 1,
                                  borderLeft: '2px solid #e0e0e0'
                                }}
                              >
                                <Autocomplete
                                  options={users}
                                  getOptionLabel={(option) => option.name}
                                  value={users.find(u => u.id === rowUserAssignments[actualRowIndex]) || null}
                                  onChange={(event, newValue) => handleRowUserAssignment(actualRowIndex, newValue?.id || null)}
                                  size="small"
                                  renderInput={(params) => (
                                    <TextField 
                                      {...params} 
                                      placeholder="Atribuie jurist"
                                      sx={{
                                        '& .MuiInputBase-root': {
                                          fontSize: '0.75rem',
                                          padding: '0 !important'
                                        },
                                        '& .MuiInputBase-input': {
                                          padding: '4px 8px !important',
                                          fontSize: '0.75rem'
                                        }
                                      }}
                                    />
                                  )}
                                  ListboxProps={{
                                    style: { fontSize: '0.75rem' }
                                  }}
                                />
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {getTotalPages() > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination 
                      count={getTotalPages()} 
                      page={currentPage} 
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}

                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', textAlign: 'center' }}>
                  Afișate: {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, getTotalRowsCount())} din {getTotalRowsCount()} rânduri × {columns.length} coloane
                  {sheetNames.length > 1 && ` | ${sheetNames.length} foi (${Object.values(selectedSheets).filter(Boolean).length} selectate)`}
                  <br />
                  <strong>Selectate:</strong> {Object.values(selectedRows).filter(Boolean).length} rânduri, {Object.values(selectedColumns).filter(Boolean).length} coloane
                </Typography>
              </>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Alert severity="info" variant="standard" sx={{ mb: 2 }}> 
              Verificați mapările coloanelor selectate înainte de a continua.
            </Alert>
            {autoGenerateNumarIntern && (
              <Alert severity="success" variant="outlined" sx={{ mb: 1, py: 0 }}>
                <Typography variant="caption" sx={{ fontSize: '11px' }}>✓ Generare automată număr intern activată</Typography>
              </Alert>
            )}

            {sheetNames.filter(name => selectedSheets[name]).map(sheetName => {
              const sheetData = sheetsData[sheetName];
              const mapping = sheetName === activeSheet 
                ? { columnMapping, selectedColumns, selectedRows }
                : (sheetMappings[sheetName] || { columnMapping: {}, selectedColumns: {}, selectedRows: {} });
              
              const sheetCols = sheetData.columns.filter(col => 
                mapping.selectedColumns[col] && mapping.columnMapping[col] && mapping.columnMapping[col] !== ''
              );

              if (sheetCols.length === 0) return null;

              return (
                <Box key={sheetName} sx={{ mb: 2 }}>
                  {sheetNames.length > 1 && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#1976d2' }}>
                      {translateSheetName(sheetName)} — {sheetCols.length} coloane mapate
                    </Typography>
                  )}
                  
                  <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#2c3e50 !important', color: 'white' }}>Nr.</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#2c3e50 !important', color: 'white' }}>Coloană Excel</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#2c3e50 !important', color: 'white' }}>Mapată la</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#2c3e50 !important', color: 'white' }}>Exemplu date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sheetCols.map((column, index) => {
                          const mappedValue = mapping.columnMapping[column];
                          const found = availableColumns.find(col => col.value === mappedValue);
                          return (
                            <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>{index + 1}</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', color: '#1976d2' }}>{column}</TableCell>
                              <TableCell sx={{ color: '#2e7d32' }}>{found ? found.label : mappedValue}</TableCell>
                              <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {sheetData.rows[0] && sheetData.rows[0][column] !== undefined && sheetData.rows[0][column] !== null 
                                  ? String(sheetData.rows[0][column]) 
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button 
                variant="contained" 
                color="warning" 
                onClick={handlePrevStep}
                startIcon={<ArrowBackIcon />}
              >
                Înapoi
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleReset}
                  startIcon={<ReplayIcon />}
                >
                  Încarcă alt fișier
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={() => { setStep(3); setCurrentPage(1); }}
                  endIcon={<ArrowForwardIcon />}
                >
                  Previzualizare date
                </Button>
              </Box>
            </Box>
          </>
        )}

        {step === 3 && (
          <>
            <Alert severity="info" variant="standard" sx={{ mb: 2 }}>
              Verificați datele finale înainte de a lansa importul. Sunt afișate doar coloanele mapate și rândurile selectate.
            </Alert>
            {autoGenerateNumarIntern && (
              <Alert severity="success" variant="outlined" sx={{ mb: 1, py: 0 }}>
                <Typography variant="caption" sx={{ fontSize: '11px' }}>✓ Generare automată număr intern activată</Typography>
              </Alert>
            )}

            {sheetNames.filter(name => selectedSheets[name]).map(sheetName => {
              const sheetData = sheetsData[sheetName];
              const mapping = sheetName === activeSheet 
                ? { columnMapping, selectedColumns, selectedRows }
                : (sheetMappings[sheetName] || { columnMapping: {}, selectedColumns: {}, selectedRows: {} });
              
              const sheetCols = sheetData.columns.filter(col => 
                mapping.selectedColumns[col] && mapping.columnMapping[col] && mapping.columnMapping[col] !== ''
              );
              const sheetSelectedRows = sheetData.rows.filter((_, index) => mapping.selectedRows[index]);

              if (sheetCols.length === 0 || sheetSelectedRows.length === 0) return null;

              return (
                <Box key={sheetName} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#1976d2' }}>
                    {translateSheetName(sheetName)} — <span style={{ fontWeight: 'normal', color: '#666' }}>{sheetSelectedRows.length} dosare × {sheetCols.length} coloane mapate</span>
                  </Typography>

                  <TableContainer component={Paper} sx={{ maxHeight: 300, width: '100%', overflowX: 'auto' }}>
                    <Table stickyHeader size="small" sx={{ tableLayout: 'auto' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#2c3e50 !important', color: 'white', minWidth: 50 }}>
                            Nr.
                          </TableCell>
                          {sheetCols.map((col, idx) => {
                            const mappedValue = mapping.columnMapping[col];
                            const found = availableColumns.find(c => c.value === mappedValue);
                            return (
                              <TableCell key={idx} sx={{ fontWeight: 'bold', backgroundColor: '#2c3e50 !important', color: 'white', minWidth: 130 }}>
                                {found ? found.label : mappedValue}
                                <Typography variant="caption" sx={{ display: 'block', color: '#aaa', fontSize: '10px' }}>
                                  ({col})
                                </Typography>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sheetSelectedRows.slice(0, 50).map((row, rowIdx) => (
                          <TableRow key={rowIdx} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                            <TableCell sx={{ fontWeight: 'bold', color: '#666', fontSize: '11px' }}>
                              {rowIdx + 1}
                            </TableCell>
                            {sheetCols.map((col, colIdx) => (
                              <TableCell key={colIdx} sx={{ fontSize: '11px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row[col] !== undefined && row[col] !== null ? String(row[col]) : '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {sheetSelectedRows.length > 50 && (
                    <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block' }}>
                      Se afișează primele 50 din {sheetSelectedRows.length} rânduri
                    </Typography>
                  )}
                </Box>
              );
            })}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Button 
                variant="contained" 
                color="warning" 
                onClick={() => { setStep(2); setCurrentPage(1); }}
                startIcon={<ArrowBackIcon />}
              >
                Înapoi la mapări
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleReset}
                  startIcon={<ReplayIcon />}
                >
                  Încarcă alt fișier
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handleStartImport}
                  startIcon={<PlayArrowIcon />}
                >
                  Începeți importul
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* Modal pentru progres import */}
      <Dialog 
        open={importProgress !== null} 
        onClose={importProgress !== 'loading' ? handleCloseJsonModal : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className='titluModal'>
          <Typography variant="h6" sx={{ color: 'white' }}>
            {importProgress === 'loading' && 'Se procesează importul...'}
            {importProgress === 'success' && 'Import realizat cu succes'}
            {importProgress === 'error' && 'Atenționare'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {importProgress === 'loading' && (
            <Box sx={{ py: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Se importă dosarele... {importCompleted} din {importTotal} procesate
                {importErrors.length > 0 && `, ${importErrors.length} necesită atenție`}
              </Typography>
              <Box sx={{ position: 'relative', mb: 1 }}>
                <Box sx={{ height: 20, borderRadius: 5, backgroundColor: '#e0e0e0', overflow: 'hidden' }}>
                  <Box sx={{ 
                    height: '100%', 
                    width: `${importPercent}%`, 
                    backgroundColor: importPercent === 100 ? '#4caf50' : '#1976d2',
                    transition: 'width 0.3s ease, background-color 0.3s ease',
                    borderRadius: 5
                  }} />
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute', top: '50%', left: '50%', 
                    transform: 'translate(-50%, -50%)', 
                    fontWeight: 'bold', fontSize: '0.875rem',
                    color: importPercent > 50 ? '#fff' : '#000'
                  }}
                >
                  {importPercent}%
                </Typography>
              </Box>
            </Box>
          )}
          {importProgress === 'success' && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {importMessage}
            </Alert>
          )}
          {importProgress === 'error' && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {importMessage}
            </Alert>
          )}
          {importErrors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning" sx={{ mb: 1 }}>
                Atenționare: {importErrors.length} {importErrors.length === 1 ? 'dosar nu a fost importat' : 'dosare nu au fost importate'}
              </Alert>
              <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Rândul</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Motiv</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Acțiune</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importErrors.map((err, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{err.row}</TableCell>
                        <TableCell sx={{ fontSize: '12px' }}>{err.message}</TableCell>
                        <TableCell>
                          <Tooltip title="Reîncearcă importul">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRetryImport(err)}
                              disabled={retryingRow === err.row}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Rezumat:</strong>
            </Typography>
            {sheetNames.length > 1 ? (
              <>
                {sheetNames.filter(name => selectedSheets[name]).map(name => {
                  const mapping = name === activeSheet
                    ? { selectedRows }
                    : (sheetMappings[name] || { selectedRows: {} });
                  const count = Object.values(mapping.selectedRows || {}).filter(Boolean).length;
                  return (
                    <Typography key={name} variant="body2" color="text.secondary">
                      • {translateSheetName(name)}: {count} rânduri selectate
                    </Typography>
                  );
                })}
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  • Total: {sheetNames.filter(name => selectedSheets[name]).reduce((total, name) => {
                    const mapping = name === activeSheet
                      ? { selectedRows }
                      : (sheetMappings[name] || { selectedRows: {} });
                    return total + Object.values(mapping.selectedRows || {}).filter(Boolean).length;
                  }, 0)} rânduri
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                • Rânduri selectate: {Object.values(selectedRows).filter(Boolean).length}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              • Coloane mapate: {sheetNames.filter(name => selectedSheets[name]).reduce((total, name) => {
                const sheetData = sheetsData[name];
                const mapping = name === activeSheet
                  ? { columnMapping, selectedColumns }
                  : (sheetMappings[name] || { columnMapping: {}, selectedColumns: {} });
                const count = sheetData.columns.filter(col => 
                  mapping.selectedColumns[col] && mapping.columnMapping[col] && mapping.columnMapping[col] !== ''
                ).length;
                return Math.max(total, count);
              }, 0)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          {importProgress === 'loading' && (
            <Button 
              onClick={() => { importCancelledRef.current = true; }} 
              color="error" 
              variant="contained"
              size="small"
            >
              Oprește importul
            </Button>
          )}
          {importProgress !== 'loading' && (
            <Button onClick={handleCloseJsonModal} color="primary">
              Închide
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog pentru Find & Replace */}
      <Dialog 
        open={showReplaceDialog} 
        onClose={handleCancelReplace}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmare înlocuire
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            S-au găsit <strong>{findResults?.count || 0}</strong> apariții ale textului "<strong>{findText}</strong>".
          </Alert>
          <Typography variant="body2" gutterBottom>
            Doriți să înlocuiți toate aparițiile cu "<strong>{replaceText}</strong>"?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            Această acțiune va modifica datele din tabel și nu poate fi anulată.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReplace} color="inherit">
            Anulează
          </Button>
          <Button onClick={handleReplace} variant="contained" color="primary">
            Înlocuiește toate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportCustom;
 